/* ============================================================
   PARCU — Cart cross-sell ("Complete the fit")
   ------------------------------------------------------------
   Seeds Shopify's Product Recommendations API from what's
   already in the cart:

     1. intent=complementary  for up to 2 cart products
        (set per product in the free Search & Discovery app)
     2. intent=related        behind them, to fill out the row

   Two things beyond that:

   A hoodie is held in second place when the cart is all tees.
   Recommendations for a tee are nearly always more tees, which
   is the one suggestion the customer least needs; the hoodie is
   the step up in basket size, so it gets the slot the eye lands
   on after the first. If the recommendations come back without
   one, a hoodie is pulled from the hoodie collection instead.

   The cards are fetched on the product page, before anything is
   added, so the block is already filled the first time the cart
   opens rather than a moment later. Fetches are remembered per
   product, so the second cart of the session costs nothing.

   Cards come from sections/parcu-cart-crosssell.liquid.
   Products already in the cart are filtered out client side.
   Adding uses the theme's own Liquid Ajax Cart product form,
   so the drawer refreshes itself with no extra wiring.
   ============================================================ */

(function () {
    'use strict';

    /* Per product rather than per cart: a cart of two tees and a cart
       of three share two of the three requests, and the warm-up on the
       product page shares all of its own. Survives the cart section
       re-rendering, which throws the element away each time. */
    var CACHE = window.PARCU_XSELL_CACHE || (window.PARCU_XSELL_CACHE = {});

    var SECTION = 'parcu-cart-crosssell';
    var HOODIES = 'collections/heavyweight-hoodie';

    function cards(html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.prototype.map.call(
            doc.querySelectorAll('[data-xsell-card]'),
            function (node) { return node.outerHTML; }
        );
    }

    function get(url, key) {
        if (CACHE[key]) return CACHE[key];
        CACHE[key] = fetch(url, { headers: { Accept: 'text/html' } })
            .then(function (res) { return res.ok ? res.text() : ''; })
            .then(cards)
            .catch(function () { return []; });
        return CACHE[key];
    }

    function recommend(endpoint, productId, intent) {
        var url =
            endpoint +
            (endpoint.indexOf('?') === -1 ? '?' : '&') +
            'section_id=' + encodeURIComponent(SECTION) +
            '&product_id=' + encodeURIComponent(productId) +
            '&limit=10' +
            '&intent=' + encodeURIComponent(intent);
        return get(url, intent + ':' + productId);
    }

    /* routes.root_url is '/' at home and '/en-nz' and the like on a
       localised domain, so it is normalised rather than concatenated
       blind. */
    function hoodies(root) {
        var base = root || '/';
        if (base.charAt(base.length - 1) !== '/') base += '/';
        return get(base + HOODIES + '?section_id=' + SECTION, 'hoodies');
    }

    function kindOf(html) {
        var m = /data-xsell-kind="([^"]*)"/.exec(html);
        return m ? m[1] : '';
    }

    function isHoodie(html) {
        return kindOf(html).indexOf('hoodie') !== -1;
    }

    function gather(endpoint, seedIds) {
        var seeds = seedIds.slice(0, 2);
        if (!seeds.length) return Promise.resolve([]);

        var jobs = seeds.map(function (id) {
            return recommend(endpoint, id, 'complementary');
        });
        jobs.push(recommend(endpoint, seeds[0], 'related'));

        return Promise.all(jobs).then(function (results) {
            /* The pairings set in Search & Discovery come first, because
               they are a decision somebody made about this garment, and
               the automatic set follows to fill the row out. These used
               to be either/or: a product with one complementary product
               against it showed one card and nothing else, which is how
               a full row became a single hoodie. Duplicates are thrown
               away later, so the overlap costs nothing. */
            var related = results.pop();
            var complementary = [];
            results.forEach(function (set) { complementary = complementary.concat(set); });
            return complementary.concat(related);
        });
    }

    /* Warm the two requests the cart will make the moment this product
       is added, while the customer is still deciding. */
    function warm(root, endpoint, productId) {
        if (!productId) return;
        recommend(endpoint, productId, 'complementary');
        recommend(endpoint, productId, 'related');
        hoodies(root);
    }

    class ParcuCartCrossSell extends HTMLElement {
        connectedCallback() {
            if (this._booted) return;
            this._booted = true;

            this.track = this.querySelector('[data-xsell-track]');
            if (!this.track) return;

            this.endpoint = this.getAttribute('endpoint') || '/recommendations/products';
            this.root = this.getAttribute('root') || '/';
            this.limit = parseInt(this.getAttribute('limit') || '6', 10);

            this.addEventListener('submit', this.onSubmit.bind(this));

            var self = this;
            var sync = function () { self.sync(); };

            sync();
            /* Liquid Ajax Cart announces every cart request it makes. */
            document.addEventListener('liquid-ajax-cart:request-end', sync);
            /* And a belt for that brace: the drawer opening is the last
               moment the block can still be filled in time. */
            var dlg = document.querySelector('#cartBox dialog');
            if (dlg && window.MutationObserver) {
                new MutationObserver(function () {
                    if (dlg.hasAttribute('open')) sync();
                }).observe(dlg, { attributes: true, attributeFilter: ['open'] });
            }
        }

        /* The cart as it is now, not as it was when the page was built. */
        sync() {
            var self = this;
            var base = this.root || '/';
            if (base.charAt(base.length - 1) !== '/') base += '/';

            return fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
                .then(function (res) { return res.ok ? res.json() : null; })
                .then(function (cart) {
                    if (!cart || !cart.items || !cart.items.length) return;

                    var ids = [];
                    var hasHoodie = false;
                    var hasOther = false;
                    cart.items.forEach(function (item) {
                        var id = String(item.product_id);
                        if (ids.indexOf(id) === -1) ids.push(id);
                        if (String(item.product_type || '').toLowerCase().indexOf('hoodie') !== -1) {
                            hasHoodie = true;
                        } else {
                            hasOther = true;
                        }
                    });

                    /* Nothing has changed, so nothing needs redrawing. */
                    var key = ids.join(',');
                    if (key === self._key) return;
                    self._key = key;

                    self.cartIds = ids;
                    self.wantHoodie = hasOther && !hasHoodie;

                    /* The size the customer has just chosen, so the
                       suggestions can be pre-set to it. */
                    var last = cart.items[cart.items.length - 1];
                    var size = '';
                    (last.options_with_values || []).forEach(function (opt) {
                        if (String(opt.name || '').toLowerCase().indexOf('size') !== -1) {
                            size = String(opt.value || '');
                        }
                    });
                    self.prefSize = size.trim().toLowerCase();
                    self.prefParts = String(last.variant_title || '')
                        .toLowerCase()
                        .split('/')
                        .map(function (part) { return part.trim(); })
                        .filter(Boolean);

                    return self.load();
                })
                .catch(function () {});
        }

        load() {
            var self = this;
            return gather(this.endpoint, this.cartIds)
                .then(function (list) {
                    if (!self.wantHoodie || list.some(isHoodie)) return list;
                    /* Nothing but tees came back. Go and get a hoodie. */
                    return hoodies(self.root).then(function (hs) {
                        return hs.length ? list.concat(hs) : list;
                    });
                })
                .then(self.paint.bind(self))
                .catch(function () {});
        }

        /* Second place, specifically: the first card is the one the
           recommendations were most confident about, and the second is
           where a different kind of garment does the most work. */
        promote(list) {
            if (!this.wantHoodie) return list;
            var at = -1;
            for (var i = 0; i < list.length; i++) {
                if (isHoodie(list[i])) { at = i; break; }
            }
            if (at < 1) return list;
            var hoodie = list.splice(at, 1)[0];
            list.splice(Math.min(1, list.length), 0, hoodie);
            return list;
        }

        paint(all) {
            if (!all || !all.length || !this.isConnected) return;

            var inCart = this.cartIds;
            var seen = {};
            var keep = [];

            /* Thin it out before ordering, so a hoodie that is already
               in the bag doesn't win the second slot and then vanish. */
            for (var i = 0; i < all.length; i++) {
                var pid = (/data-product-id="([^"]*)"/.exec(all[i]) || [])[1];
                if (!pid || seen[pid] || inCart.indexOf(pid) !== -1) continue;
                seen[pid] = true;
                keep.push(all[i]);
            }
            if (!keep.length) return;

            keep = this.promote(keep).slice(0, this.limit);

            var frag = document.createDocumentFragment();
            var holder = document.createElement('div');
            var shown = 0;
            for (var j = 0; j < keep.length; j++) {
                holder.innerHTML = keep[j];
                var card = holder.firstElementChild;
                if (!card) continue;
                this.prefill(card);
                frag.appendChild(card);
                shown++;
            }
            if (!shown) return;

            this.track.innerHTML = '';
            this.track.appendChild(frag);
            this.removeAttribute('hidden');
            this.reposition();
        }

        /* Pick the variant that matches what's already in the bag, so the
           customer doesn't have to re-state a size they've just chosen.
           Requires the size to match exactly; other options (fit, colour)
           only break ties. Falls back to leaving the picker visible. */
        prefill(card) {
            var select = card.querySelector('.parcu-xsell__select');
            if (!select || !this.prefSize) return;

            var wanted = this.prefSize;
            var prefs = this.prefParts;
            var best = null;
            var bestScore = -1;

            Array.prototype.forEach.call(select.options, function (option) {
                if (!option.value) return;

                var parts = option.text.toLowerCase().split('/').map(function (p) {
                    return p.trim();
                });
                if (parts.indexOf(wanted) === -1) return;

                var score = parts.filter(function (p) {
                    return prefs.indexOf(p) !== -1;
                }).length;

                if (score > bestScore) {
                    bestScore = score;
                    best = option;
                }
            });

            if (best) {
                select.value = best.value;
                card.classList.add('is-prefilled');
            }
        }

        /* On a phone the block otherwise lands below the payment icons,
           past the point anyone scrolls. Move it above them. */
        reposition() {
            if (!window.matchMedia('(max-width: 777px)').matches) return;

            var footer = this.closest('coretex-cart');
            var checkout = footer && footer.querySelector('cart-footer .gocheckout');
            if (!checkout) return;

            var icons = Array.prototype.filter.call(checkout.children, function (child) {
                return child.querySelectorAll('img, svg').length >= 4;
            })[0];
            if (!icons || icons.previousElementSibling === this) return;

            checkout.insertBefore(this, icons);
        }

        onSubmit(event) {
            var card = event.target.closest('[data-xsell-card]');
            if (!card) return;

            card.classList.add('is-adding');

            /* Liquid Ajax Cart re-renders the whole cart section on
               success, which removes this node. The timeout only
               matters if the request fails silently. */
            setTimeout(function () {
                card.classList.remove('is-adding');
            }, 6000);
        }
    }

    if (!customElements.get('parcu-cart-crosssell')) {
        customElements.define('parcu-cart-crosssell', ParcuCartCrossSell);
    }

    /* The product page hands over its own id. Fetching here, rather than
       when the drawer opens, is what puts the cards in the block on the
       first look instead of the second. */
    var seed = window.PARCU_XSELL_SEED;
    if (seed && seed.id) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function () {
                warm(seed.root, seed.endpoint, seed.id);
            });
        } else {
            warm(seed.root, seed.endpoint, seed.id);
        }
    }
}());
